import { create } from "zustand"
import {
  isImageTaskFailedStatus,
  isImageTaskProcessingStatus,
  isImageTaskSucceededStatus,
} from "~/common/constants"
import {
  getImageTaskResultUrl,
  pollHotVideoStep,
} from "~/common/tasks/hotVideoTask"
import { sleep } from "~/common/utils"
import { withPointBalanceRefresh } from "~/common/billing/pointAction"
import {
  createHotVideoWhiteImage,
  createWhiteImageTask,
  listWhiteImageTasks,
} from "~/api/task"

type OpenWhiteBgImageOptions = {
  targetShopId?: number
}

type WhiteBgImageState = {
  errorMessage: string
  loading: boolean
  open: boolean
  originImageUrl: string
  resultUrl: string
  targetShopId?: number
  taskId: string
  closeModal: () => void
  generateWhiteBgImage: (options?: {
    hotVideoTaskId?: string
  }) => Promise<string | undefined>
  openModal: (originImageUrl: string, options?: OpenWhiteBgImageOptions) => void
  reset: () => void
}

const pollSegmentTask = async (
  taskId: string,
  getState: () => WhiteBgImageState,
  attempt = 1,
): Promise<API.Task.ImageTask | undefined> => {
  const maxAttempts = 80

  if (!getState().open) return undefined
  if (attempt > maxAttempts) {
    throw new Error("生成白底图超时，请稍后重试")
  }

  const { data } = await listWhiteImageTasks({
    page: 0,
    page_size: 100,
  })
  const task = data?.items.find((item) => {
    return item.id === taskId || item.task_id === taskId
  })

  if (!task) {
    await sleep(3000)
    return pollSegmentTask(taskId, getState, attempt + 1)
  }

  if (isImageTaskProcessingStatus(task.status)) {
    await sleep(3000)
    return pollSegmentTask(taskId, getState, attempt + 1)
  }

  if (isImageTaskSucceededStatus(task.status)) {
    return task
  }

  if (isImageTaskFailedStatus(task.status)) {
    throw new Error(task.error_message || "生成白底图失败")
  }

  return task
}

export const useWhiteBgImageStore = create<WhiteBgImageState>((set, get) => ({
  errorMessage: "",
  loading: false,
  open: false,
  originImageUrl: "",
  resultUrl: "",
  taskId: "",

  openModal: (originImageUrl, options) => {
    set({
      errorMessage: "",
      loading: false,
      open: true,
      originImageUrl,
      resultUrl: "",
      targetShopId: options?.targetShopId,
      taskId: "",
    })
  },

  closeModal: () => {
    set({
      errorMessage: "",
      loading: false,
      open: false,
      resultUrl: "",
      taskId: "",
    })
  },

  reset: () => {
    set({
      errorMessage: "",
      loading: false,
      open: false,
      originImageUrl: "",
      resultUrl: "",
      targetShopId: undefined,
      taskId: "",
    })
  },

  generateWhiteBgImage: async (options) => {
    const { loading, originImageUrl } = get()

    if (loading || !originImageUrl) return undefined

    set({
      errorMessage: "",
      loading: true,
      resultUrl: "",
    })

    try {
      const requestParams = {
        size: "1:1",
        image_urls: [originImageUrl],
      } satisfies API.Task.CreateWhiteImageTaskParams

      const taskResult = options?.hotVideoTaskId
        ? await (async () => {
            const hotVideoTaskId = options.hotVideoTaskId

            if (!hotVideoTaskId) {
              throw new Error("创建白底图任务失败")
            }

            const { data } = await withPointBalanceRefresh(() =>
              createHotVideoWhiteImage({
                ...requestParams,
                task_id: hotVideoTaskId,
              }),
            )
            const imageTask = data?.white_image

            if (!imageTask) {
              throw new Error("创建白底图任务失败")
            }

            set({ taskId: hotVideoTaskId })

            return isImageTaskSucceededStatus(imageTask.status)
              ? imageTask
              : pollHotVideoStep<API.Task.ImageTaskInfo>({
                  taskId: hotVideoTaskId,
                  selectStep: (task) => task.white_image,
                  isProcessing: (step) =>
                    isImageTaskProcessingStatus(step.status),
                  isFailed: (step) => isImageTaskFailedStatus(step.status),
                  getErrorMessage: (step) =>
                    step.error_message || "生成白底图失败",
                  timeoutMessage: "生成白底图超时，请稍后重试",
                })
          })()
        : await (async () => {
            const { data } = await withPointBalanceRefresh(() =>
              createWhiteImageTask(requestParams),
            )
            const taskId = data?.id || data?.task_id

            if (!data || !taskId) {
              throw new Error("创建白底图任务失败")
            }

            set({ taskId })

            return isImageTaskSucceededStatus(data.status)
              ? data
              : pollSegmentTask(taskId, get)
          })()
      const segmentUrl = getImageTaskResultUrl(taskResult)

      if (!taskResult) {
        return undefined
      }

      if (!segmentUrl) {
        throw new Error("生成结果中没有图片地址")
      }

      set({
        resultUrl: segmentUrl,
      })
      return segmentUrl
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "生成白底图失败"
      set({ errorMessage })
      throw error
    } finally {
      set({ loading: false })
    }
  },
}))
